import { createStoreSchema, type StoreDto } from '@cartly/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  PageHeader,
  SelectField,
  Spinner,
  TextField,
  useToast,
} from '@cartly/ui';
import { Plus, Store } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { StatusBadge } from '../../components/Common';
import { formatRelative } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';
import { CURRENCIES } from '../regions';
import { CONNECTABLE_PLATFORMS, PLATFORM_LABELS } from './platforms';

function ConnectStoreModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const submit = useSubmit();
  const [form, setForm] = useState({
    name: '',
    platform: 'woocommerce',
    domain: '',
    currency: CURRENCIES.find((c) => c.value === 'INR') ? 'INR' : 'USD',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = createStoreSchema.safeParse(form);
    if (!parsed.success) {
      const fields: Record<string, string> = {};
      for (const issue of parsed.error.issues) fields[String(issue.path[0])] ??= issue.message;
      submit.setFieldErrors(fields);
      return;
    }
    const created = await submit.run(() => api.post<StoreDto>('/stores', parsed.data));
    if (created) navigate(`/stores/${created.id}`);
  };

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      open
      title="Connect a store"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="store-form" loading={submit.pending}>
            Continue
          </Button>
        </>
      }
    >
      <form id="store-form" className="stack" onSubmit={onSubmit} noValidate>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <TextField
          label="Store name"
          value={form.name}
          onChange={set('name')}
          error={submit.fieldErrors['name']}
          autoFocus
        />
        <SelectField
          label="Platform"
          value={form.platform}
          options={CONNECTABLE_PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))}
          onChange={set('platform')}
          hint="Shopify stores connect through the Cartly360 Shopify app."
          error={submit.fieldErrors['platform']}
        />
        <TextField
          label="Store domain"
          placeholder="shop.example.com"
          value={form.domain}
          onChange={set('domain')}
          error={submit.fieldErrors['domain']}
        />
        <div className="form-grid">
          <SelectField
            label="Currency"
            value={form.currency}
            options={CURRENCIES}
            onChange={set('currency')}
          />
          <TextField
            label="Time zone"
            value={form.timezone}
            onChange={set('timezone')}
            error={submit.fieldErrors['timezone']}
          />
        </div>
      </form>
    </Modal>
  );
}

export function StoresPage() {
  const session = useActiveSession();
  const { can, refresh } = useSession();
  const toast = useToast();
  const otherOrgs = session.tenants.filter((t) => t.id !== session.activeTenant?.id);
  const switchTo = async (tenantId: string) => {
    try {
      await api.post('/tenants/switch', { tenantId });
      await refresh();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const [connecting, setConnecting] = useState(false);
  const { data, error, loading } = useLoader(
    () => api.get<{ items: StoreDto[] }>('/stores').then((r) => r.items),
    session.activeTenant?.id,
  );

  const action = can('stores:manage') && (
    <Button variant="primary" icon={<Plus size={16} />} onClick={() => setConnecting(true)}>
      Connect store
    </Button>
  );

  return (
    <>
      <PageHeader
        title="Stores"
        description="Connect WooCommerce, custom PHP/Laravel, Python and Node.js stores through the Universal Commerce API."
        actions={action}
      />
      {loading && !data ? (
        <div className="page-loading">
          <Spinner size="lg" label="Loading stores" />
        </div>
      ) : error || !data ? (
        <Alert tone="critical">{error ?? 'Failed to load stores'}</Alert>
      ) : data.length === 0 ? (
        <Card>
          <EmptyState icon={<Store size={22} />} title="No stores connected yet" action={action}>
            Connect your first store to start syncing products, customers, carts and orders.
          </EmptyState>
        </Card>
      ) : (
        <div className="store-grid">
          {data.map((s) => (
            <Link key={s.id} to={`/stores/${s.id}`} className="card store-card">
              <div className="row-between">
                <span className="store-card__name">{s.name}</span>
                <StatusBadge
                  status={s.status}
                  label={s.status === 'pending' ? 'awaiting events' : s.status}
                />
              </div>
              <div className="store-card__meta">
                <Badge>{PLATFORM_LABELS[s.platform]}</Badge>
                <span className="mono text-sm">{s.domain}</span>
              </div>
              <dl className="store-card__stats">
                <div>
                  <dt>Products</dt>
                  <dd>{s.counts?.products ?? 0}</dd>
                </div>
                <div>
                  <dt>Orders</dt>
                  <dd>{s.counts?.orders ?? 0}</dd>
                </div>
                <div>
                  <dt>Customers</dt>
                  <dd>{s.counts?.customers ?? 0}</dd>
                </div>
              </dl>
              <div className="row-between text-sm text-muted">
                <span>
                  {s.lastEventAt ? `Last event ${formatRelative(s.lastEventAt)}` : 'No events yet'}
                </span>
                {(s.counts?.failedEvents ?? 0) > 0 && (
                  <Badge tone="critical">{s.counts!.failedEvents} failed</Badge>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
      {data && data.length === 0 && otherOrgs.length > 0 && (
        <Alert tone="info" title="Looking for a store you connected earlier?">
          Stores belong to an organization, and you are a member of{' '}
          {otherOrgs.length === 1 ? 'another one' : 'others'}.
          <div className="row">
            {otherOrgs.map((t) => (
              <Button key={t.id} size="sm" variant="secondary" onClick={() => void switchTo(t.id)}>
                Switch to {t.name}
              </Button>
            ))}
          </div>
        </Alert>
      )}
      {connecting && <ConnectStoreModal onClose={() => setConnecting(false)} />}
    </>
  );
}
